<template>
	<div class="container mgs-stats-page pb-4">
		<h1>Statistics</h1>
		<div v-if="fetchingCourses">Fetching courses....</div>
		<div v-else>
			<div class="flex justify-between">
				<div>
					<p class="font-bold mr-1">Course</p>
					<select v-model="filteredCourse">
						<option value="all">All</option>
						<option
							v-for="course in courses"
							:key="`stat-course-select-${course.uuid}`"
							:value="course.uuid"
							>{{ course.name }}</option
						>
					</select>
				</div>
				<div class="">
					<p class="font-bold mr-1">Modes</p>
					<select v-model="compsMode">
						<option value="all">All</option>
						<option value="comps">Comps Only</option>
						<option value="non">Non Comps Only</option>
					</select>
				</div>

				<div>
					<p class="font-bold mr-1">Category</p>
					<select v-model="typeMode">
						<option value="all">All</option>
						<option value="scores">Scores</option>
						<option value="driving">Driving</option>
						<option value="gir">GIR</option>
						<option value="putting">Putting</option>
						<option value="scrambling">Scrambling</option>
					</select>
				</div>
			</div>

			<div class="flex justify-end text-center text-xl font-bold">
				<div v-if="filteredRounds.length === 0">No rounds available</div>
				<statistics-partials-plain
					v-else
					partial-title="Rounds Played"
					:partial-value="filteredRounds.length"
				></statistics-partials-plain>
			</div>
			<table class="table w-full">
				<thead>
					<tr>
						<th></th>
						<th :key="`stats-year-${year}`" v-for="year in roundYearRange">
							{{ year }}
						</th>
						<th>Last Round</th>
						<th>Last 3 rounds</th>
						<th>Last 6 Rounds</th>
						<th>All</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<th>Rounds Played</th>
						<th :key="`stats-year-${year}-score-avg`" v-for="year in roundYearRange">
							{{ yearStats(year).roundsPlayedFiltered }}
						</th>
						<th colspan="3"></th>
						<th>{{ filteredRounds.length }}</th>
					</tr>
					<template v-if="['all', 'scoring'].includes(typeMode)">
						<tr class="mgs-stats-table-subhead">
							<th :colspan="roundYearRange.length + 5">SCORING</th>
						</tr>
						<statistics-table-head
							:round-year-range="roundYearRange"
						></statistics-table-head>
						<tr>
							<th>Score Average</th>
							<td
								:key="`stats-year-${year}-score-avg`"
								v-for="year in roundYearRange"
							>
								{{ yearStats(year).scoreAvg }}
							</td>
							<td>{{ lastRound.scoreAvg }}</td>
							<td>{{ lastThreeRounds.scoreAvg }}</td>
							<td>{{ lastSixRounds.scoreAvg }}</td>
							<td>{{ allRounds.scoreAvg }}</td>
						</tr>

						<tr>
							<th>Total To Par</th>
							<td :key="`stats-year-${year}-to-par`" v-for="year in roundYearRange">
								{{ yearStats(year).totalScoring.score_literal }}
							</td>

							<td>{{ lastRound.totalScoring.score_literal }}</td>
							<td>{{ lastThreeRounds.totalScoring.score_literal }}</td>
							<td>{{ lastSixRounds.totalScoring.score_literal }}</td>

							<td>{{ allRounds.totalScoring.score_literal }}</td>
						</tr>

						<tr>
							<th>Average To Par</th>
							<td :key="`stats-year-${year}-to-par`" v-for="year in roundYearRange">
								{{ yearStats(year).toParAvg }}
							</td>
							<td>{{ lastRound.toParAvg }}</td>
							<td>{{ lastThreeRounds.toParAvg }}</td>
							<td>{{ lastSixRounds.toParAvg }}</td>
							<td>{{ allRounds.toParAvg }}</td>
						</tr>

						<template>
							<tr :key="`score-type-${scoreType}`" v-for="scoreType in scoreTypes">
								<th>{{ scoreType }} Per Round</th>
								<td
									:key="`stats-year-${year}-to-par`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalScoring[`${scoreType}_per_round`] }}
								</td>
								<td>{{ lastRound.totalScoring[`${scoreType}_per_round`] }}</td>
								<td>{{ lastThreeRounds.totalScoring[`${scoreType}_per_round`] }}</td>
								<td>{{ lastSixRounds.totalScoring[`${scoreType}_per_round`] }}</td>
								<td>{{ allRounds.totalScoring[`${scoreType}_per_round`] }}</td>
							</tr>
						</template>
						<template v-for="par in [3, 4, 5]">
							<tr :key="`par-average-${par}`">
								<th>{{ `Par ${par} Average` }}</th>
								<td
									:key="`stats-year-${year}-to-par`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalScoring[`par_${par}_average`] }}
								</td>
								<td>{{ lastRound.totalScoring[`par_${par}_average`] }}</td>
								<td>{{ lastThreeRounds.totalScoring[`par_${par}_average`] }}</td>
								<td>{{ lastSixRounds.totalScoring[`par_${par}_average`] }}</td>
								<td>{{ allRounds.totalScoring[`par_${par}_average`] }}</td>
							</tr>
							<tr :key="`par-tally--${par}`">
								<th>{{ `Par ${par} To Par` }}</th>
								<td
									:key="`stats-year-${year}-to-par`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalScoring[`par_${par}_to_par`] }}
								</td>
								<td>{{ lastRound.totalScoring[`par_${par}_to_par`] }}</td>
								<td>{{ lastThreeRounds.totalScoring[`par_${par}_to_par`] }}</td>
								<td>{{ lastSixRounds.totalScoring[`par_${par}_to_par`] }}</td>
								<td>{{ allRounds.totalScoring[`par_${par}_to_par`] }}</td>
							</tr>
							<template v-if="false" v-for="scoreType in scoreTypes">
								<tr :key="`par-score-type--${par}=${scoreType}`">
									<th>{{ `Par ${par} ${scoreType}` }}</th>
									<td
										:key="`stats-year-${year}-par-score-type-${scoreType}`"
										v-for="year in roundYearRange"
									>
										{{
											`${
												yearStats(year).totalScoring[
													`par_${par}_${scoreType}`
												]
											}/${yearStats(year)[`par${par}splayed`]} `
										}}
									</td>
									<td>
										{{
											`${lastRound.totalScoring[`par_${par}_${scoreType}`]}/${
												lastRound[`par${par}splayed`]
											} `
										}}
									</td>
									<td>
										{{
											`${
												lastThreeRounds.totalScoring[
													`par_${par}_${scoreType}`
												]
											}/${lastThreeRounds[`par${par}splayed`]} `
										}}
									</td>
									<td>
										{{
											`${
												lastSixRounds.totalScoring[
													`par_${par}_${scoreType}`
												]
											}/${lastSixRounds[`par${par}splayed`]} `
										}}
									</td>
									<td>
										{{
											`${allRounds.totalScoring[`par_${par}_${scoreType}`]}/${
												allRounds[`par${par}splayed`]
											} `
										}}
									</td>
								</tr>
							</template>
						</template>
					</template>
					<template v-if="['all', 'driving'].includes(typeMode)">
						<tr class="mgs-stats-table-subhead">
							<th :colspan="roundYearRange.length + 5">DRIVING</th>
						</tr>
						<statistics-table-head
							:round-year-range="roundYearRange"
						></statistics-table-head>
						<tr>
							<th>Fairways Hit</th>
							<td
								:key="`stats-year-${year}-fairways-hit`"
								v-for="year in roundYearRange"
							>
								{{ yearStats(year).totalDriving.fairways_hit }}/{{
									yearStats(year).totalDriving.fairway_chances
								}}
								({{ yearStats(year).totalDriving.fairways_percentage }}%)
							</td>
							<td>
								{{ lastRound.totalDriving.fairways_hit }}/{{
									lastRound.totalDriving.fairway_chances
								}}
								({{ lastRound.totalDriving.fairways_percentage }}%)
							</td>
							<td>
								{{ lastThreeRounds.totalDriving.fairways_hit }}/{{
									lastThreeRounds.totalDriving.fairway_chances
								}}
								({{ lastThreeRounds.totalDriving.fairways_percentage }}%)
							</td>
							<td>
								{{ lastSixRounds.totalDriving.fairways_hit }}/{{
									lastSixRounds.totalDriving.fairway_chances
								}}
								({{ lastSixRounds.totalDriving.fairways_percentage }}%)
							</td>
							<td>
								{{ allRounds.totalDriving.fairways_hit }}/{{
									allRounds.totalDriving.fairway_chances
								}}
								({{ allRounds.totalDriving.fairways_percentage }}%)
							</td>
						</tr>

						<tr>
							<th>Fairways Missed</th>
							<td
								:key="`stats-year-${year}-fairways-hit`"
								v-for="year in roundYearRange"
							>
								{{ yearStats(year).totalDriving.fairways_missed }}/{{
									yearStats(year).totalDriving.fairway_chances
								}}
								({{ yearStats(year).totalDriving.fairways_missed_percentage }}%)
							</td>
							<td>
								{{ lastRound.totalDriving.fairways_missed }}/{{
									lastRound.totalDriving.fairway_chances
								}}
								({{ lastRound.totalDriving.fairways_missed_percentage }}%)
							</td>
							<td>
								{{ lastThreeRounds.totalDriving.fairways_missed }}/{{
									lastThreeRounds.totalDriving.fairway_chances
								}}
								({{ lastThreeRounds.totalDriving.fairways_missed_percentage }}%)
							</td>
							<td>
								{{ lastSixRounds.totalDriving.fairways_missed }}/{{
									lastSixRounds.totalDriving.fairway_chances
								}}
								({{ lastSixRounds.totalDriving.fairways_missed_percentage }}%)
							</td>
							<td>
								{{ allRounds.totalDriving.fairways_missed }}/{{
									allRounds.totalDriving.fairway_chances
								}}
								({{ allRounds.totalDriving.fairways_missed_percentage }}%)
							</td>
						</tr>

						<template v-for="(dir, index) in missedDrivingDirections">
							<tr :key="`stats-year-fairways-missed-${index}`">
								<th>Fairways Missed {{ dir }}</th>
								<td
									:key="`stats-year-${year}-fairways-hit-${index}`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalDriving[`fairways_missed_${dir}`] }}/{{
										yearStats(year).totalDriving.fairways_missed
									}}
									({{
										yearStats(year).totalDriving[
											`fairways_missed_${dir}_percentage`
										]
									}}%)
								</td>
								<td>
									{{ lastRound.totalDriving[`fairways_missed_${dir}`] }}/{{
										lastRound.totalDriving.fairways_missed
									}}
									({{
										lastRound.totalDriving[`fairways_missed_${dir}_percentage`]
									}}%)
								</td>
								<td>
									{{ lastThreeRounds.totalDriving[`fairways_missed_${dir}`] }}/{{
										lastThreeRounds.totalDriving.fairways_missed
									}}
									({{
										lastThreeRounds.totalDriving[
											`fairways_missed_${dir}_percentage`
										]
									}}%)
								</td>
								<td>
									{{ lastSixRounds.totalDriving[`fairways_missed_${dir}`] }}/{{
										lastSixRounds.totalDriving.fairways_missed
									}}
									({{
										lastSixRounds.totalDriving[
											`fairways_missed_${dir}_percentage`
										]
									}}%)
								</td>
								<td>
									{{ allRounds.totalDriving[`fairways_missed_${dir}`] }}/{{
										allRounds.totalDriving.fairways_missed
									}}
									({{
										allRounds.totalDriving[`fairways_missed_${dir}_percentage`]
									}}%)
								</td>
							</tr>
						</template>
					</template>
					<template v-if="['all', 'gir'].includes(typeMode)">
						<tr class="mgs-stats-table-subhead">
							<th :colspan="roundYearRange.length + 5">APPROACH PLAY</th>
						</tr>
						<statistics-table-head
							:round-year-range="roundYearRange"
						></statistics-table-head>
						<tr>
							<th>GIR PER RD</th>
							<td :key="`stats-year-${year}-gir-hit`" v-for="year in roundYearRange">
								{{ (yearStats(year).totalGir.gir /
									yearStats(year).roundsPlayedFiltered).toFixed(2)
								}}
							</td>
							<td>
								{{ (lastRound.totalGir.gir / lastRound.roundsPlayedFiltered).toFixed(2) }}
							</td>
							<td>
								{{ (lastThreeRounds.totalGir.gir /
									lastThreeRounds.roundsPlayedFiltered).toFixed(2)
								}}
							</td>
							<td>
								{{ (lastSixRounds.totalGir.gir /
									lastSixRounds.roundsPlayedFiltered).toFixed(2)
								}}
							</td>
							<td>
								{{ (allRounds.totalGir.gir / allRounds.roundsPlayedFiltered).toFixed(2) }}
							</td>
						</tr>
						<tr>
							<th>GIR %</th>
							<td :key="`stats-year-${year}-gir-hit`" v-for="year in roundYearRange">
								{{ yearStats(year).totalGir.gir }}/{{
									yearStats(year).holesPlayedFiltered
								}}
								({{ yearStats(year).totalGir.gir_percentage }}%)
							</td>
							<td>
								{{ lastRound.totalGir.gir }}/{{ lastRound.holesPlayedFiltered }} ({{
									lastRound.totalGir.gir_percentage
								}}%)
							</td>
							<td>
								{{ lastThreeRounds.totalGir.gir }}/{{
									lastThreeRounds.holesPlayedFiltered
								}}
								({{ lastThreeRounds.totalGir.gir_percentage }}%)
							</td>
							<td>
								{{ lastSixRounds.totalGir.gir }}/{{
									lastSixRounds.holesPlayedFiltered
								}}
								({{ lastSixRounds.totalGir.gir_percentage }}%)
							</td>
							<td>
								{{ allRounds.totalGir.gir }}/{{ allRounds.holesPlayedFiltered }} ({{
									allRounds.totalGir.gir_percentage
								}}%)
							</td>
						</tr>
						<template v-for="par in [3, 4, 5]">
							<tr :key="`par-gir-${par}`">
								<th>{{ `Par ${par} GIR` }}</th>
								<td
									:key="`stats-year-${year}-par-gir-${par}`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalGir[`par_${par}_hit`] }}/{{
										yearStats(year).totalGir[`par_${par}_chances`]
									}}
									({{ yearStats(year).totalGir[`par_${par}_percentage`] }}%)
								</td>
								<td>
									{{ lastRound.totalGir[`par_${par}_hit`] }}/{{
										lastRound.totalGir[`par_${par}_chances`]
									}}
									({{ lastRound.totalGir[`par_${par}_percentage`] }}%)
								</td>
								<td>
									{{ lastThreeRounds.totalGir[`par_${par}_hit`] }}/{{
										lastThreeRounds.totalGir[`par_${par}_chances`]
									}}
									({{ lastThreeRounds.totalGir[`par_${par}_percentage`] }}%)
								</td>
								<td>
									{{ lastSixRounds.totalGir[`par_${par}_hit`] }}/{{
										lastSixRounds.totalGir[`par_${par}_chances`]
									}}
									({{ lastSixRounds.totalGir[`par_${par}_percentage`] }}%)
								</td>
								<td>
									{{ allRounds.totalGir[`par_${par}_hit`] }}/{{
										allRounds.totalGir[`par_${par}_chances`]
									}}
									({{ allRounds.totalGir[`par_${par}_percentage`] }}%)
								</td>
							</tr>
						</template>
					</template>
					<template v-if="['all', 'scrambling'].includes(typeMode)">
						<tr class="mgs-stats-table-subhead">
							<th :colspan="roundYearRange.length + 5">SCRAMBLING</th>
						</tr>
						<statistics-table-head
							:round-year-range="roundYearRange"
						></statistics-table-head>
						<template v-for="row in scrambleRows">
							<tr :key="`scrambling-${row.key}`">
								<th>{{ row.title }}</th>
								<td
									:key="`stats-year-${year}-scrambling-${row.key}`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalScrambling[`${row.key}_percentage`] }}% ({{
										yearStats(year).totalScrambling[`${row.key}_success`]
									}}/{{ yearStats(year).totalScrambling[`${row.key}_chances`] }})
								</td>
								<td>
									{{ lastRound.totalScrambling[`${row.key}_percentage`] }}% ({{
										lastRound.totalScrambling[`${row.key}_success`]
									}}/{{ lastRound.totalScrambling[`${row.key}_chances`] }})
								</td>
								<td>
									{{ lastThreeRounds.totalScrambling[`${row.key}_percentage`] }}% ({{
										lastThreeRounds.totalScrambling[`${row.key}_success`]
									}}/{{ lastThreeRounds.totalScrambling[`${row.key}_chances`] }})
								</td>
								<td>
									{{ lastSixRounds.totalScrambling[`${row.key}_percentage`] }}% ({{
										lastSixRounds.totalScrambling[`${row.key}_success`]
									}}/{{ lastSixRounds.totalScrambling[`${row.key}_chances`] }})
								</td>
								<td>
									{{ allRounds.totalScrambling[`${row.key}_percentage`] }}% ({{
										allRounds.totalScrambling[`${row.key}_success`]
									}}/{{ allRounds.totalScrambling[`${row.key}_chances`] }})
								</td>
							</tr>
						</template>
					</template>
					<template v-if="['all', 'putting'].includes(typeMode)">
						<tr class="mgs-stats-table-subhead">
							<th :colspan="roundYearRange.length + 5">PUTTING</th>
						</tr>
						<statistics-table-head
							:round-year-range="roundYearRange"
						></statistics-table-head>
						<template v-for="row in puttRows">
							<tr :key="`putting-${row.key}`">
								<th>{{ row.title }}</th>
								<td
									:key="`stats-year-${year}-putting-${row.key}`"
									v-for="year in roundYearRange"
								>
									{{ yearStats(year).totalPutting[row.key] }}
								</td>
								<td>{{ lastRound.totalPutting[row.key] }}</td>
								<td>{{ lastThreeRounds.totalPutting[row.key] }}</td>
								<td>{{ lastSixRounds.totalPutting[row.key] }}</td>
								<td>{{ allRounds.totalPutting[row.key] }}</td>
							</tr>
						</template>
					</template>
				</tbody>
			</table>
		</div>
	</div>
</template>
<script>
/** @format */
import courses from "./../modules/courses";

function getDifference(difference) {
	if (difference > 0) {
		return `+${difference}`;
	} else if (difference === 0) {
		return "L";
	} else {
		return difference;
	}
}

export default {
	name: "RoundsStatistics",
	props: {
		golfRounds: {
			type: Array,
			required: true
		}
	},
	components: {
		"statistics-partials-plain": () =>
			import(
				/* webpackChunkName: "StatisticsPlain" */ "@/components/Statistics/Partials/Plain"
			),
		"statistics-table-head": () =>
			import(
				/* webpackChunkName: "StatisticsTableHead" */ "@/components/Statistics/TableHead"
			),
		"statistics-by-par": () =>
			import(/* webpackChunkName: "StatisticsByPar" */ "@/components/Statistics/ByPar"),
		"statistics-fairways": () =>
			import(/* webpackChunkName: "StatisticsFairways" */ "@/components/Statistics/Fairways"),
		"statistics-greens": () =>
			import(/* webpackChunkName: "StatisticsGreens" */ "@/components/Statistics/Greens"),
		"statistics-putts": () =>
			import(/* webpackChunkName: "StatisticsPutts" */ "@/components/Statistics/Putts"),
		"statistics-scoring": () =>
			import(/* webpackChunkName: "StatisticsScoring" */ "@/components/Statistics/Scoring"),
		"statistics-scrambling": () =>
			import(
				/* webpackChunkName: "StatisticsScrambling" */ "@/components/Statistics/Scrambling"
			)
	},
	methods: {
		yearStats(year) {
			return this.roundStats(this.roundsByYear[year]);
		},
		roundStats(rounds) {
			if (rounds === undefined) rounds = [];
			const stats = {
				totalGross: 0,
				roundsPlayedTotal: this.golfRounds.length,
				roundsPlayedFiltered: rounds.length,
				holesPlayedFiltered: rounds.length * 18,
				toPar: 0,
				scoreAvg: undefined,
				toParAvg: undefined,
				roundYears: [],
				par3splayed: 0,
				par4splayed: 0,
				par5splayed: 0,
				totalScoring: {
					albatrosses: 0,
					eagles: 0,
					birdies: 0,
					pars: 0,
					bogeys: 0,
					double_bogeys: 0,
					triple_or_worse: 0,
					albatrosses_per_round: 0,
					eagles_per_round: 0,
					birdies_per_round: 0,
					pars_per_round: 0,
					bogeys_per_round: 0,
					double_bogeys_per_round: 0,
					triple_or_worse_per_round: 0,
					current_score: 0,
					par_3_scores: 0,
					par_4_scores: 0,
					par_5_scores: 0,
					par_3_average: 0,
					par_4_average: 0,
					par_5_average: 0,
					par_3_to_par: 0,
					par_4_to_par: 0,
					par_5_to_par: 0,
					par_3_albatrosses: 0,
					par_3_eagles: 0,
					par_3_birdies: 0,
					par_3_pars: 0,
					par_3_bogeys: 0,
					par_3_double_bogeys: 0,
					par_3_triple_or_worse: 0,
					par_3_penalties: 0,
					par_4_albatrosses: 0,
					par_4_eagles: 0,
					par_4_birdies: 0,
					par_4_pars: 0,
					par_4_bogeys: 0,
					par_4_double_bogeys: 0,
					par_4_triple_or_worse: 0,
					par_4_penalties: 0,
					par_5_albatrosses: 0,
					par_5_eagles: 0,
					par_5_birdies: 0,
					par_5_pars: 0,
					par_5_bogeys: 0,
					par_5_double_bogeys: 0,
					par_5_triple_or_worse: 0,
					par_5_penalties: 0,
					par: 0,
					par_so_far: 0,
					penalties: 0,
					score: 0,
					difference: 0,
					score_literal: ""
				},
				totalGir: {
					gir: 0,
					gir_percentage: 0,
					par_3_chances: 0,
					par_3_hit: 0,
					par_3_percentage: 0,
					par_4_chances: 0,
					par_4_hit: 0,
					par_4_percentage: 0,
					par_5_chances: 0,
					par_5_hit: 0,
					par_5_percentage: 0
				},
				totalPutting: {
					four_putts: 0,
					no_putts: 0,
					one_putts: 0,
					putts_per_gir: 0,
					putts_per_hole: 0,
					three_putts: 0,
					total_gir_putts: 0,
					total_putts: 0,
					two_putts: 0,
					putts_per_round: 0
				},
				totalDriving: {
					fairway_chances: 0,
					fairways_hit: 0,
					fairways_missed: 0,
					fairways_missed_left: 0,
					fairways_missed_left_percentage: 0,
					fairways_missed_long: 0,
					fairways_missed_long_percentage: 0,
					fairways_missed_percentage: 0,
					fairways_missed_right: 0,
					fairways_missed_right_percentage: 0,
					fairways_missed_short: 0,
					fairways_missed_short_percentage: 0,
					fairways_missed_unknown: 0,
					fairways_percentage: 0
				},
				totalScrambling: {
					bunker_chances: 0,
					bunker_percentage: 0,
					bunker_success: 0,
					chip_chances: 0,
					chip_percentage: 0,
					chip_success: 0,
					full_shot_chances: 0,
					full_shot_percentage: 0,
					full_shot_success: 0,
					off_green_putt_chances: 0,
					off_green_putt_percentage: 0,
					off_green_putt_success: 0,
					pitch_chances: 0,
					pitch_percentage: 0,
					pitch_success: 0,
					total_chances: 0,
					total_percentage: 0,
					total_success: 0,
					unknown_chances: 0,
					unknown_percentage: 0,
					unknown_success: 0
				}
			};

			const len = rounds.length;
			for (let i = 0; i < len; i++) {
				const round = rounds[i];
				stats.totalGross += round.gross_score;
				stats.toPar += round.to_par;
				for (let sc in stats.totalScoring) {
					if (
						sc.indexOf([
							"par_3_average",
							"par_4_average",
							"par_5_average",
							"score_literal",
							"albatrosses_per_round",
							"eagles_per_round",
							"birdies_per_round",
							"pars_per_round",
							"bogeys_per_round",
							"double_bogeys_per_round",
							"triple_or_worse_per_round"
						]) == -1
					) {
						if (round.total_scoring[sc] != undefined) {
							stats.totalScoring[sc] += round.total_scoring[sc];
						}
					}
				}
				for (let dr in stats.totalDriving) {
					if (
						[
							"fairways_missed_left_percentage",
							"fairways_missed_long_percentage",
							"fairways_missed_percentage",
							"fairways_missed_right_percentage",
							"fairways_missed_short_percentage",
							"fairways_percentage"
						].indexOf(dr) == -1
					) {
						stats.totalDriving[dr] += round.total_driving[dr];
					}
				}
				for (let putts in stats.totalPutting) {
					if (
						putts != "putts_per_gir" &&
						putts != "putts_per_hole" &&
						putts != "putts_per_round"
					) {
						stats.totalPutting[putts] += round.total_putting[putts];
					}
				}
				for (let scr in stats.totalScrambling) {
					if (
						[
							"bunker_percentage",
							"chip_percentage",
							"full_shot_percentage",
							"off_green_putt_percentage",
							"pitch_percentage",
							"total_percentage",
							"unknown_percentage"
						].indexOf(scr) == -1
					) {
						stats.totalScrambling[scr] += round.total_scrambling[scr];
					}
				}
				stats.totalGir.gir += round.total_gir.gir;
				stats.totalGir.par_3_chances += round.total_gir.par_3_chances;

				stats.totalGir.par_3_hit += round.total_gir.par_3_hit;
				stats.totalGir.par_4_chances += round.total_gir.par_4_chances;
				stats.totalGir.par_4_hit += round.total_gir.par_4_hit;
				stats.totalGir.par_5_chances += round.total_gir.par_5_chances;
				stats.totalGir.par_5_hit += round.total_gir.par_5_hit;
			}
			stats.totalScoring.score_literal = getDifference(stats.totalScoring.difference);
			stats.par3splayed = stats.totalGir.par_3_chances;
			stats.par4splayed = stats.totalGir.par_4_chances;
			stats.par5splayed = stats.totalGir.par_5_chances;

			for (let type of this.scoreTypes) {
				stats.totalScoring[`${type}_per_round`] = `${(stats.totalScoring[type] / rounds.length).toFixed(2)}`;
			}
			
			stats.totalGir.gir_percentage = (
				(stats.totalGir.gir / stats.holesPlayedFiltered) *
				100
			).toFixed(2);
			stats.totalGir.par_3_percentage = (
				(stats.totalGir.par_3_hit / stats.totalGir.par_3_chances) *
				100
			).toFixed(2);
			stats.totalGir.par_4_percentage = (
				(stats.totalGir.par_4_hit / stats.totalGir.par_4_chances) *
				100
			).toFixed(2);
			stats.totalGir.par_5_percentage = (
				(stats.totalGir.par_5_hit / stats.totalGir.par_5_chances) *
				100
			).toFixed(2);
			stats.totalScoring.par_3_average = (
				stats.totalScoring.par_3_scores / stats.totalGir.par_3_chances
			).toFixed(2);
			stats.totalScoring.par_4_average = (
				stats.totalScoring.par_4_scores / stats.totalGir.par_4_chances
			).toFixed(2);
			stats.totalScoring.par_5_average = (
				stats.totalScoring.par_5_scores / stats.totalGir.par_5_chances
			).toFixed(2);
			stats.totalScoring.par_3_to_par = getDifference(
				stats.totalScoring.par_3_scores - stats.par3splayed * 3
			);
			stats.totalScoring.par_4_to_par = getDifference(
				stats.totalScoring.par_4_scores - stats.par4splayed * 4
			);
			stats.totalScoring.par_5_to_par = getDifference(
				stats.totalScoring.par_5_scores - stats.par5splayed * 5
			);
			stats.scoreAvg = (stats.totalGross / len).toFixed(2);
			stats.toParAvg = (stats.toPar / len).toFixed(2);

			stats.totalPutting.putts_per_gir = (
				stats.totalPutting.total_gir_putts / stats.totalGir.gir
			).toFixed(2);
			stats.totalPutting.putts_per_hole = (
				stats.totalPutting.total_putts / stats.holesPlayedFiltered
			).toFixed(2);
			stats.totalPutting.putts_per_round = (stats.totalPutting.total_putts / len).toFixed(2);

			stats.totalDriving.fairways_percentage = (
				(stats.totalDriving.fairways_hit / stats.totalDriving.fairway_chances) *
				100
			).toFixed(2);
			stats.totalDriving.fairways_missed_percentage = (
				(stats.totalDriving.fairways_missed / stats.totalDriving.fairway_chances) *
				100
			).toFixed(2);
			stats.totalDriving.fairways_missed_left_percentage = (
				(stats.totalDriving.fairways_missed_left / stats.totalDriving.fairways_missed) *
				100
			).toFixed(2);
			stats.totalDriving.fairways_missed_right_percentage = (
				(stats.totalDriving.fairways_missed_right / stats.totalDriving.fairways_missed) *
				100
			).toFixed(2);
			stats.totalDriving.fairways_missed_short_percentage = (
				(stats.totalDriving.fairways_missed_short / stats.totalDriving.fairways_missed) *
				100
			).toFixed(2);
			stats.totalDriving.fairways_missed_long_percentage = (
				(stats.totalDriving.fairways_missed_long / stats.totalDriving.fairways_missed) *
				100
			).toFixed(2);

			stats.totalScrambling.total_percentage = (
				(stats.totalScrambling.total_success / stats.totalScrambling.total_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.bunker_percentage = (
				(stats.totalScrambling.bunker_success / stats.totalScrambling.bunker_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.chip_percentage = (
				(stats.totalScrambling.chip_success / stats.totalScrambling.chip_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.full_shot_percentage = (
				(stats.totalScrambling.full_shot_success /
					stats.totalScrambling.full_shot_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.off_green_putt_percentage = (
				(stats.totalScrambling.off_green_putt_success /
					stats.totalScrambling.off_green_putt_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.pitch_percentage = (
				(stats.totalScrambling.pitch_success / stats.totalScrambling.pitch_chances) *
				100
			).toFixed(2);
			stats.totalScrambling.unknown_percentage = (
				(stats.totalScrambling.unknown_success / stats.totalScrambling.unknown_chances) *
				100
			).toFixed(2);

			return stats;
		},
		calculateStats(year) {},
		filterByCourse(rd) {
			if (this.filteredCourse == "all") return true;
			return this.filteredCourse == rd.course_id;
		},
		filterByYear(rd) {
			if (this.filteredYear == "all") return true;
			return this.filteredYear == rd.date_played_year;
		},
		filterByComps(rd) {
			if (this.compsMode == "comps") return rd.competition === true;
			if (this.compsMode == "non") return rd.competition === false;
			return true;
		},
		setYearRange() {
			const roundYears = new Set();
			const len = this.golfRounds.length;
			for (let i = 0; i < len; i++) {
				const round = this.filteredRounds[i];
				roundYears.add(round.date_played_year);
			}
			this.roundYearRange = Array.from(roundYears.values());
		}
	},
	computed: {
		lastRound() {
			return this.roundStats(this.filteredRounds.slice(0, 1));
		},
		lastThreeRounds() {
			const lastThreeRounds = this.filteredRounds.slice(0, 3);
			return this.roundStats(lastThreeRounds);
		},
		lastSixRounds() {
			const lastSixRounds = this.filteredRounds.slice(0, 6);
			return this.roundStats(lastSixRounds);
		},
		allRounds() {
			return this.roundStats(this.filteredRounds);
		},
		roundsByYear() {
			const years = {};
			for (let rd of this.filteredRounds) {
				if (!years[rd.date_played_year]) years[rd.date_played_year] = [];
				years[rd.date_played_year].push(rd);
			}
			return years;
		},
		filteredRounds() {
			return this.golfRounds
				.filter(rd => this.filterByCourse(rd))
				.filter(rd => this.filterByYear(rd))
				.filter(rd => this.filterByComps(rd));
		},
		stats() {
			return this.roundStats(this.filteredRounds);
		}
	},
	mounted() {
		this.setYearRange();
		courses.getAll().then(courseList => {
			this.courses = courseList.filter(c => {
				return this.golfRounds.find(r => c.uuid === r.course_id);
			});
			this.fetchingCourses = false;
		});
	},
	data() {
		return {
			mode: "editForm",
			filteredYear: "all",
			filteredCourse: "all",
			roundYearRange: undefined,
			compsMode: "all",
			typeMode: "all",
			fetchingCourses: true,
			courses: [],
			scoreTypes: [
				"albatrosses",
				"eagles",
				"birdies",
				"pars",
				"bogeys",
				"double_bogeys",
				"triple_or_worse"
			],
			missedDrivingDirections: [
				// "short", 
				// "long", 
				"left", 
				"right"
			],
			puttRows: [
				{
					title: "Putts Per Round",
					key: "putts_per_round"
				},
				{
					title: "Putts Per GIR",
					key: "putts_per_gir"
				},
				{
					title: "Putts Per Hole",
					key: "putts_per_hole"
				},
				{
					title: "0 Putt Greens",
					key: "no_putts"
				},
				{
					title: "1 Putt Greens",
					key: "one_putts"
				},
				{
					title: "2 Putt Greens",
					key: "two_putts"
				},
				{
					title: "3 Putt Greens",
					key: "three_putts"
				},
				{
					title: "4 Putt Greens",
					key: "four_putts"
				}
			],
			scrambleRows: [
				{
					title: "Total Scrambles",
					key: "total"
				},
				{
					title: "Chip Scrambles",
					key: "chip"
				},
				{
					title: "Pitch Scrambles",
					key: "pitch"
				},
				{
					title: "Sand Saves",
					key: "bunker"
				},
				{
					title: "Full Shot Scrambles",
					key: "full_shot"
				},
				{
					title: "Off Green Putt",
					key: "off_green_putt"
				},
				{
					title: "Unknown Scrambles",
					key: "unknown"
				}
			]
		};
	}
};
</script>
<style lang="scss">
/** @format */

.mgs-stats-page {
	.mgs-plain-statbox {
		@apply mb-1;
	}
}
.mgs-stats-table-subhead {
	background: $forestGreen;

	th {
		text-align: center !important;
		color: white;
		font-size: 1.5rem;
	}
}
</style>
